import { baseApi } from "../baseApi";
import { pick } from "../util";
import type { Order, CreateOrderInput, UpdateOrderInput } from "../types";

const ORDER_FIELDS = `
  id
  organizationId
  orderNumber
  factoryId
  warehouseId
  orderDate
  items {
    productId
    productName
    quantity
    unitPrice
    lineTotal
  }
  subtotal
  taxPercentage
  taxAmount
  total
  status
  notes
  createdAt
  updatedAt
`;

export const orderApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrders: builder.query<Order[], string | void>({
      query: (organizationId) => ({
        body: {
          query: `
            query Orders($organizationId: String) {
              orders(organizationId: $organizationId) {
                ${ORDER_FIELDS}
              }
            }
          `,
          variables: { organizationId: organizationId ?? null },
        },
      }),

      transformResponse: (res: unknown) => pick<Order[]>(res, "orders"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((o) => ({ type: "Order" as const, id: o.id })),
              { type: "Order" as const, id: "LIST" },
            ]
          : [{ type: "Order" as const, id: "LIST" }],
    }),

    getOrder: builder.query<Order, string>({
      query: (id) => ({
        body: {
          query: `
            query Order($id: String!) {
              order(id: $id) {
                ${ORDER_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<Order>(res, "order"),

      providesTags: (_result, _err, id) => [{ type: "Order", id }],
    }),

    createOrder: builder.mutation<Order, CreateOrderInput>({
      query: (input) => ({
        body: {
          query: `
            mutation CreateOrder($input: CreateOrderInput!) {
              createOrder(input: $input) {
                ${ORDER_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<Order>(res, "createOrder"),

      invalidatesTags: [{ type: "Order", id: "LIST" }],
    }),

    updateOrder: builder.mutation<
      Order,
      { id: string; input: UpdateOrderInput }
    >({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateOrder($id: String!, $input: UpdateOrderInput!) {
              updateOrder(id: $id, input: $input) {
                ${ORDER_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) => pick<Order>(res, "updateOrder"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "Order", id },
        { type: "Order", id: "LIST" },
      ],
    }),

    deleteOrder: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeleteOrder($id: String!) {
              deleteOrder(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "deleteOrder"),

      invalidatesTags: (_result, _err, id) => [
        { type: "Order", id },
        { type: "Order", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetOrdersQuery,
  useLazyGetOrdersQuery,
  useGetOrderQuery,
  useLazyGetOrderQuery,
  useCreateOrderMutation,
  useUpdateOrderMutation,
  useDeleteOrderMutation,
} = orderApi;
